// Global Instructions Rule Applied!
//
// Account form — Co-investments tab (Phase 4c, signed-off Mockup 4). Shown
// for accounts carrying the Co-investor role (added by the role engine when
// the account is a linked holder on a reviewed cap-table version). One row
// per class they hold, latest reviewed snapshot per company.
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { Card, Spin, Table, Tag } from 'antd'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { CoInvestmentRow, fetchAccountCoInvestments } from '../services/dealExecutionService.ts'
import { pct, shares } from './CapTableTab.tsx'

interface AccountCoInvestmentsTabProps {
  accountId: string | null
}

export default function AccountCoInvestmentsTab({
  accountId
}: AccountCoInvestmentsTabProps): React.ReactElement {
  const { openFormInTab } = useTabManagerContext()
  const [rows, setRows] = useState<CoInvestmentRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!accountId) return
    let cancelled = false
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const result = await fetchAccountCoInvestments(token, accountId)
      if (!cancelled) {
        setRows(result.data)
        setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [accountId])

  if (loading) {
    return (
      <div className='flex justify-center py-16'>
        <Spin />
      </div>
    )
  }

  return (
    <div className='px-2 py-4'>
      <Card size='small' title='Co-investments'>
        {rows.length === 0 ? (
          <div className='text-sm text-gray-400 py-2'>
            No co-investments recorded yet. Rows appear here when this account is added as a
            round investor on a deal (amount) or linked as a holder on a company&apos;s cap
            table (shareholding).
          </div>
        ) : (
          <Table
            size='small'
            rowKey={(r) =>
              `${r.accounts__id}-${r.cap_table_versions__id ?? 'amount-only'}-${r.cap_table_lines__class_name ?? ''}`
            }
            pagination={false}
            dataSource={rows}
            columns={[
              {
                title: 'Company',
                dataIndex: 'accounts__name',
                render: (v, row) => (
                  <span className='cursor-pointer text-blue-600 hover:underline'
                    onClick={() =>
                      openFormInTab('companies', row.accounts__id as string, (v as string) || 'Company')
                    }
                  >
                    {v || 'Company'}
                  </span>
                )
              },
              { title: 'Round', dataIndex: 'rounds__name', render: (v) => v || '—' },
              {
                title: 'As Of',
                dataIndex: 'cap_table_versions__as_of_date',
                render: (v) => (v ? dayjs(String(v)).format('MM/DD/YYYY') : '—')
              },
              { title: 'Class', dataIndex: 'cap_table_lines__class_name', render: (v) => v || '—' },
              {
                // Company-level sum of this account's round participations —
                // shown on the company's first class row only so multi-class
                // holdings don't read as multiple investments.
                title: 'Amount Invested',
                key: 'amount',
                align: 'right' as const,
                render: (_, row, index) => {
                  const firstForCompany =
                    index === 0 || rows[index - 1]?.accounts__id !== row.accounts__id
                  const amount = row.round_participations__amount as number | null | undefined
                  return (
                    <span className='tabular-nums'>
                      {firstForCompany && amount != null
                        ? Number(amount).toLocaleString('en-US', { maximumFractionDigits: 2 })
                        : '—'}
                    </span>
                  )
                }
              },
              {
                title: 'Issued',
                dataIndex: 'cap_table_holdings__issued',
                align: 'right' as const,
                render: (v) => shares(v as number)
              },
              {
                title: 'FD',
                dataIndex: 'cap_table_holdings__fully_diluted',
                align: 'right' as const,
                render: (v) => shares(v as number)
              },
              {
                title: '% of Class',
                dataIndex: 'prog__pct_of_class',
                align: 'right' as const,
                render: (v) => (v != null ? pct(v as number) : '—')
              },
              {
                title: 'Snapshot',
                dataIndex: 'cap_table_versions__status',
                render: (v) =>
                  v == null ? (
                    <Tag color='blue'>no holdings yet</Tag>
                  ) : (
                    <Tag color={v === 'current' ? 'green' : 'default'}>{String(v)}</Tag>
                  )
              }
            ]}
          />
        )}
      </Card>
    </div>
  )
}
